import { Photo } from '../types';
import { motion, useInView } from 'motion/react';
import { useRef, useMemo, useSyncExternalStore } from 'react';
import { getOptimizedImageUrl } from '../utils/imageOptimization';

interface GridGalleryProps {
  photos: Photo[];
  onViewDetails: (photo: Photo) => void;
  onBack?: () => void;
  categoryTitle?: string;
}

// Row patterns: each number is a flex-grow ratio for that item in the row.
// This creates visual rhythm: 2 wide → 3 even → 1 big + 2 small → 4 even → 2 uneven → 3 shifted...
// Every row fills 100% width with no gaps.
const rowPatterns: number[][] = [
  [1.6, 1],        // 2 items: wider left
  [1, 1, 1],       // 3 equal
  [2, 1, 1],       // 3 items: one big, two small
  [1, 1, 1, 1],    // 4 equal
  [1, 1.6],        // 2 items: wider right
  [1, 1, 2],       // 3 items: two small, one big
  [1, 1, 1],       // 3 equal
  [1.4, 1, 1.4],   // 3 items: ends wider
  [1, 1, 1, 1],    // 4 equal
  [1, 2],          // 2 items: big right
  [1, 1, 1],       // 3 equal
  [2, 1],          // 2 items: big left
];

// Fixed row height for uniform look (vh-based for responsiveness)
const rowHeights = [
  '50vh', '40vh', '45vh', '35vh', '50vh', '45vh',
  '40vh', '42vh', '35vh', '50vh', '40vh', '48vh',
];

// Mobile row patterns: only 1 or 2 items per row, randomly mixed
const mobileRowPatterns: number[][] = [
  [1],              // 1 full-width
  [1, 1],           // 2 equal
  [1.4, 1],         // 2 items: wider left
  [1],              // 1 full-width
  [1, 1.4],         // 2 items: wider right
  [1, 1],           // 2 equal
  [1],              // 1 full-width
  [1, 1],           // 2 equal
  [1],              // 1 full-width
  [1.3, 1],         // 2 items: slightly wider left
  [1],              // 1 full-width
  [1, 1.3],         // 2 items: slightly wider right
];

const mobileRowHeights = [
  '35vh', '28vh', '30vh', '38vh', '26vh', '32vh',
  '36vh', '28vh', '34vh', '30vh', '40vh', '26vh',
];

// Seeded pseudo-random to shuffle mobile patterns consistently per photo count
function seededShuffle<T>(arr: T[], seed: number): T[] {
  const shuffled = [...arr];
  let s = seed;
  for (let i = shuffled.length - 1; i > 0; i--) {
    s = (s * 16807 + 0) % 2147483647;
    const j = s % (i + 1);
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// Simple media query hook
const mobileQuery = typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)') : null;
function subscribeToMobile(cb: () => void) {
  mobileQuery?.addEventListener('change', cb);
  return () => mobileQuery?.removeEventListener('change', cb);
}
function getIsMobile() {
  return mobileQuery?.matches ?? false;
}

interface RowData {
  photos: Photo[];
  pattern: number[];
  height: string;
  startIndex: number;
}

function buildRows(photos: Photo[], isMobile: boolean): RowData[] {
  const rows: RowData[] = [];
  let photoIndex = 0;
  let patternIndex = 0;

  if (isMobile) {
    // Shuffle mobile patterns based on photo count for a "random" but stable layout
    const shuffled = seededShuffle(
      mobileRowPatterns.map((p, i) => ({ pattern: p, height: mobileRowHeights[i % mobileRowHeights.length] })),
      photos.length * 7 + 31
    );

    while (photoIndex < photos.length) {
      const entry = shuffled[patternIndex % shuffled.length];
      const count = Math.min(entry.pattern.length, photos.length - photoIndex);
      const rowPhotos = photos.slice(photoIndex, photoIndex + count);
      const rowPattern = entry.pattern.slice(0, count);

      rows.push({ photos: rowPhotos, pattern: rowPattern, height: entry.height, startIndex: photoIndex });
      photoIndex += count;
      patternIndex++;
    }
  } else {
    while (photoIndex < photos.length) {
      const pattern = rowPatterns[patternIndex % rowPatterns.length];
      const height = rowHeights[patternIndex % rowHeights.length];
      const count = Math.min(pattern.length, photos.length - photoIndex);
      const rowPhotos = photos.slice(photoIndex, photoIndex + count);
      const rowPattern = pattern.slice(0, count);

      rows.push({ photos: rowPhotos, pattern: rowPattern, height, startIndex: photoIndex });
      photoIndex += count;
      patternIndex++;
    }
  }

  return rows;
}

function GalleryItem({
  photo, flexGrow, height, globalIndex, indexInRow,
  onViewDetails,
}: {
  photo: Photo; flexGrow: number; height: string;
  globalIndex: number; indexInRow: number;
  onViewDetails: (photo: Photo) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: false, margin: "-6%" });
  const isPriority = globalIndex < 5;
  const delay = indexInRow * 0.08;
  const isWide = flexGrow >= 1.6;

  return (
    <motion.div
      ref={ref}
      className="group relative cursor-pointer overflow-hidden bg-neutral-900"
      style={{ flex: `${flexGrow} 1 0%`, height }}
      onClick={() => onViewDetails(photo)}
      initial={{ opacity: 0, y: 40, scale: 0.96 }}
      animate={isInView ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 40, scale: 0.96 }}
      transition={{
        duration: 0.85,
        delay: isInView ? delay : 0,
        ease: [0.25, 0.1, 0.25, 1],
      }}
    >
      <img
        src={getOptimizedImageUrl(photo.imageUrl, isWide ? 'preview' : 'thumbnail', photo.thumbnailUrl)}
        alt={`${photo.title} — ${photo.category} fine art print by Keyiscreation`}
        loading={isPriority ? "eager" : "lazy"}
        decoding="async"
        width={isWide ? 800 : 400}
        height={isWide ? 533 : 400}
        className="w-full h-full object-cover transition-[filter,transform] duration-700 group-hover:scale-105"
      />

      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-500" />

      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
        <div className="text-center text-white px-4">
          <h3 className={`text-white mb-1 ${isWide ? 'text-base md:text-lg' : 'text-sm'}`}>
            {photo.title}
          </h3>
          <p className="text-xs text-white/80 tracking-wide uppercase">{photo.category}</p>
        </div>
      </div>
    </motion.div>
  );
}

export function GridGallery({ photos, onViewDetails, onBack, categoryTitle }: GridGalleryProps) {
  const isMobile = useSyncExternalStore(subscribeToMobile, getIsMobile, () => false);
  const rows = useMemo(() => buildRows(photos, isMobile), [photos, isMobile]);

  if (photos.length === 0) {
    return (
      <section className="bg-black min-h-screen flex flex-col">
        {onBack && (
          <div className="sticky top-[120px] lg:top-[44px] z-30 flex justify-end px-4 py-2 pointer-events-none">
            <button
              onClick={onBack}
              className="pointer-events-auto text-white/40 hover:text-yellow-400 transition-colors p-2"
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18" /><path d="m6 6 12 12" />
              </svg>
            </button>
          </div>
        )}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center px-8">
            <p
              className="text-white/60 text-xl tracking-wide mb-2"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              No photos in this collection yet
            </p>
            <p className="text-white/30 text-sm">Check back soon for new additions.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-black min-h-screen">
      {/* Floating X close button */}
      {onBack && (
        <div className="sticky top-[120px] lg:top-[44px] z-30 flex justify-end px-4 py-2 pointer-events-none">
          <button
            onClick={onBack}
            className="pointer-events-auto text-white/90 hover:text-yellow-400 transition-colors p-2"
            aria-label="Close"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18" /><path d="m6 6 12 12" />
            </svg>
          </button>
        </div>
      )}

      <div className="flex flex-col gap-[2px]">
        {rows.map((row, rowIndex) => (
          <div key={`row-${rowIndex}`} className="flex gap-[2px]">
            {row.photos.map((photo, i) => (
              <GalleryItem
                key={`grid-${photo.id}`}
                photo={photo}
                flexGrow={row.pattern[i]}
                height={row.height}
                globalIndex={row.startIndex + i}
                indexInRow={i}
                onViewDetails={onViewDetails}
              />
            ))}
          </div>
        ))}
      </div>

      <div className="w-full flex items-center justify-center py-12">
        <motion.p
          className="max-w-2xl mx-auto text-white/85 tracking-wide leading-relaxed text-lg md:text-base font-light text-center px-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 1, ease: [0.25, 0.1, 0.25, 1] }}
        >
          Limited edition photographic prints on museum-quality fine art paper.{' '}
          Each piece comes with a certificate of authenticity and edition number.
        </motion.p>
      </div>
    </section>
  );
}

export default GridGallery;